import { useCallback, useEffect, useState } from "react";
import { useConnectionAdapter } from "../ConnectionRegistryProvider";
import type { ServiceKey } from "./types";
import type { ConnectActionProps } from "../../shared/components/StubModule";

/**
 * Builds the `connectAction` prop for a stub module from the registry adapter.
 * Returns undefined when no adapter is registered for the service (e.g. Studio
 * settings haven't loaded, or the connection is missing authConfig) — the stub
 * then renders without a Connect button.
 */
export function useConnectAction(
  service: ServiceKey,
  label: string,
): ConnectActionProps | undefined {
  const adapter = useConnectionAdapter(service);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connected, setConnected] = useState<boolean>(adapter?.isConnected() ?? false);

  useEffect(() => {
    if (!adapter) {
      setConnected(false);
      return;
    }
    setConnected(adapter.isConnected());
    const unsubscribe = adapter.onTokenChange((t) => setConnected(!!t));
    
    if (!adapter.isConnected()) {
      adapter.silentConnect().catch(() => {});
    }
    return unsubscribe;
  }, [adapter]);

  const onClick = useCallback(() => {
    if (!adapter) return;
    setBusy(true);
    setError(null);
    adapter
      .connect()
      .catch((e: unknown) => {
        const msg = e instanceof Error ? e.message : "Sign-in failed";
        setError(msg);
      })
      .finally(() => setBusy(false));
  }, [adapter, service]);

  const disconnect = useCallback(() => {
    if (!adapter) return;
    setError(null);
    try {
      adapter.disconnect();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Sign-out failed";
      setError(msg);
    }
  }, [adapter, service]);

  if (!adapter) return undefined;

  return { label, onClick, disconnect, busy, error, connected };
}
